import { getTwoLetterCountryCode } from "@faire/localization/core/consts/THREE_TO_TWO_LETTER_COUNTRY_ISO_CODE";
import { getLanguageCode } from "@faire/localization/core/utils";
import { ILocaleKey } from "@faire/types-localization-api/indigofair/locale/ILocaleKey";
import { isNotUndefined } from "@faire/typescript";

const SCRIPT_SUBTAG = /^[A-Za-z]{4}$/;
const REGION_SUBTAG = /^(?:[A-Za-z]{2}|\d{3})$/;

/**
 * Region subtag of a BCP 47 tag, e.g. `IT` for `it-IT` or `TW` for
 * `zh-Hant-TW`. Only the position after the language (and optional script) is
 * a region, so extension subtags such as the `nu` in `it-u-nu-latn` are not.
 */
export const getLocaleRegion = (
  locale: string | undefined
): string | undefined => {
  const subtags = locale?.split(/[-_]/).slice(1) ?? [];
  const candidate = SCRIPT_SUBTAG.test(subtags[0] ?? "")
    ? subtags[1]
    : subtags[0];
  return candidate && REGION_SUBTAG.test(candidate)
    ? candidate.toUpperCase()
    : undefined;
};

/**
 * Locale used to format money: the UI language plus the viewer's country.
 *
 * The country comes from the region of the backend `currency_format_locale`
 * (which is the viewer's country default locale, e.g. `it-IT`), falling back to
 * the locale key's country. An Italian retailer browsing in English therefore
 * formats with `en-IT` (`€175`) rather than `it-IT` (`175 €`).
 *
 * Falls back to the backend locale unchanged when the UI language is unknown.
 */
export const buildCurrencyFormatLocale = ({
  localeKey,
  currencyFormatLocale,
}: {
  localeKey: ILocaleKey | undefined;
  currencyFormatLocale: string | undefined;
}): string | undefined => {
  const languageCode = localeKey?.language
    ? getLanguageCode(localeKey)
    : undefined;
  if (!localeKey || !languageCode) {
    return currencyFormatLocale || undefined;
  }

  const region =
    getLocaleRegion(currencyFormatLocale) ??
    (localeKey.country
      ? getTwoLetterCountryCode(localeKey.country)?.toUpperCase()
      : undefined);

  return [languageCode, region].filter(isNotUndefined).join("-");
};
